"use client"

import React, { useState, useEffect, useCallback, useMemo } from "react"
import { SlidersHorizontal, Search, Plus } from "lucide-react"
import OwnerInfoMobile from "@/features/homeowners/components/owner-info-mobile"
import { Avatar, AvatarImage } from "../../../shared/components/ui/avatar"
import { AvatarFallback } from "@radix-ui/react-avatar"

import { Button } from "../../../shared/components/ui/button"
import { Badge } from "../../../shared/components/ui/badge"
import { Switch } from "../../../shared/components/ui/switch"
import Link from "next/link"
import { Pencil, MapPin } from "lucide-react"
import WorkOrderDeleteDialogWrapper from "./work-order-delete-dialog-wrapper"
import { WORK_ORDERS_ROUTES, DASHBOARD_ROUTE, HOMEOWNERS_ROUTES } from "../../../shared/constants/pages-routes"
import { useRegionAwareHref } from "@/shared/hooks/use-region-aware-href"
import { buildHomeownersDetailUrlWithReturn } from "../../../shared/lib/utils/navigation-helpers"
import { buildRegionAwareHref } from "../../../shared/lib/utils"
import { useRegion } from "@/shared/hooks/use-region"
import { useWorkOrdersList } from "../hooks/use-work-orders.hook"
import { buildGoogleMapsPlaceUrl } from "@/shared/lib/utils/google-maps-url"
import WorkOrdersSearch from "./work-orders-search"
import WorkOrdersFilterButton from "./work-orders-filter-button"
import WorkOrdersFilterIndicator from "./work-orders-filter-indicator"
import { useSession } from "../../auth/context/session.context"
import { UserRole } from "../../../shared/constants/user-roles"
import { updateWorkOrderStatus } from "../actions"
import {
  maskHomeownerLastName,
  maskHomeownerPhone,
  maskHomeownerAddress,
} from "@/shared/lib/utils/homeowner-privacy.utils"
import errorToast from "../../../shared/lib/toast/error.toast"
import successToast from "../../../shared/lib/toast/success.toast"
import { formatWorkScope } from "../utils/format-work-scope"
import { WorkOrder } from "../types/work-order.type"

// Helper for tel: link from phone (used for Phone Number on cards)
function getPhoneTelLink(phone: string | null | undefined): string | null {
    if (!phone) return null
    return `tel:${phone.replace(/[^\d+]/g, "")}`
}

// Status Toggle Button (Open/Closed, like Confidential On/Off)
function StatusToggleSwitch({
    workOrderId,
    currentStatus,
    onStatusChange,
}: {
    workOrderId: number
    currentStatus: string
    onStatusChange?: (workOrderId: number, newStatus: string) => void
}) {
    const [isLoading, setIsLoading] = useState(false)
    const [localStatus, setLocalStatus] = useState(currentStatus)
    const [isOptimistic, setIsOptimistic] = useState(false)

    useEffect(() => {
        if (!isOptimistic) {
            setLocalStatus(currentStatus)
        }
    }, [currentStatus, isOptimistic])

    const isActive = localStatus === "Active"

    const handleClick = async () => {
        if (!onStatusChange) return

        setIsLoading(true)
        const newStatus = isActive ? "Inactive" : "Active"

        setLocalStatus(newStatus)
        setIsOptimistic(true)

        try {
            await onStatusChange(workOrderId, newStatus)
        } catch (error) {
            console.error("Failed to update work order status:", error)
            setLocalStatus(currentStatus)
            setIsOptimistic(false)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Button
            type="button"
            variant={isActive ? "default" : "outline"}
            size="sm"
            className="shrink-0"
            onClick={handleClick}
            disabled={isLoading}
            aria-label={isActive ? "Close work order" : "Reopen work order"}
        >
            {isActive ? "Open" : "Closed"}
        </Button>
    )
}

function WorkOrdersCardsMobile() {
    const { data, isLoading, queryParams } = useWorkOrdersList()
    const { regionSlug } = useRegion()

    // Get session for role-based access control
    const { session, profile } = useSession()
    const canManageWorkOrders =
        session?.role === UserRole.ADMIN ||
        session?.role === UserRole.BASE_LEADER

    // Privacy: hide filters for Volunteer, Homeowner, Supporter (search only)
    const hideFilters = [
        UserRole.VOLUNTEER,
        UserRole.HOMEOWNER,
        UserRole.Supporter,
    ].includes(session?.role as UserRole)
    
    // Get region-aware href for create work order
    const createHref = useRegionAwareHref(DASHBOARD_ROUTE.createWorkOrders)

    // Get work orders directly from API response (no client-side filtering)
    const workOrders = useMemo(() => {
        if (!data?.success || !data.data?.workOrders) {
            return []
        }
        return data.data.workOrders
    }, [data?.data?.workOrders, data?.success])
    const hasActiveFilters = useMemo(() => {
        const ignoredKeys = new Set(["page", "limit", "sortBy", "sortOrder"])
        return Object.entries(queryParams as Record<string, unknown>).some(
            ([key, value]) =>
                !ignoredKeys.has(key) &&
                value !== undefined &&
                value !== null &&
                value !== "" &&
                !(Array.isArray(value) && value.length === 0)
        )
    }, [queryParams])
    const isVolunteerOrTeamLeader =
        session?.role === UserRole.VOLUNTEER ||
        session?.role === UserRole.TEAM_LEADER
    const emptyStateMessage =
        hasActiveFilters && isVolunteerOrTeamLeader
            ? "You have not been assigned to any Work Orders filtered."
            : session?.role === UserRole.VOLUNTEER
                ? "You are not assigned to any Work Orders"
                : "No data found"

    // Handle work order status change
    const handleStatusChange = useCallback(
        async (workOrderId: number, newStatus: string) => {
            try {
                const result = await updateWorkOrderStatus(
                    workOrderId,
                    newStatus
                )
                if (result.success) {
                    successToast(`Work order status updated to ${newStatus === "Active" ? "Open" : "Closed"}`)
                    // The table will automatically refresh due to cache invalidation
                } else {
                    errorToast(
                        result.message || "Failed to update work order status"
                    )
                }
            } catch (error) {
                errorToast("Failed to update work order status")
                console.error("Error updating work order status:", error)
            }
        },
        []
    )

    return (
        <div className="block xl:hidden space-y-3 mb-10">
            <div className="flex flex-col gap-3">
                {/* Mobile: Filter (hidden for V/HO/Supporter), search, Create Work Order */}
                <div className="flex items-center gap-2 w-full">
                    {!hideFilters && <WorkOrdersFilterButton />}
                    <div className="flex-1">
                        <WorkOrdersSearch />
                    </div>
                    {canManageWorkOrders && (
                        <Link href={createHref || DASHBOARD_ROUTE.createWorkOrders}>
                            <Button 
                                variant="default" 
                                size="sm"
                                className="bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10 flex-shrink-0"
                            >
                                <Plus className="w-5 h-5" />
                                <span className="hidden sm:inline">Create Work Order</span>
                            </Button>
                        </Link>
                    )}
                </div>
            </div>
            {!hideFilters && <WorkOrdersFilterIndicator />}
            <div className="space-y-3">
                {isLoading ? (
                    // Loading state with skeleton cards
                    Array.from({ length: 5 }).map((_, index) => (
                        <div
                            key={`loading-${index}`}
                            className="border p-4 space-y-4 rounded"
                        >
                            <div className="grid grid-cols-1">
                                <div className="h-4 bg-muted animate-pulse rounded" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="h-4 bg-muted animate-pulse rounded" />
                                <div className="h-4 bg-muted animate-pulse rounded" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="h-4 bg-muted animate-pulse rounded" />
                                <div className="h-4 bg-muted animate-pulse rounded" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="h-4 bg-muted animate-pulse rounded" />
                                <div className="h-4 bg-muted animate-pulse rounded" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="h-4 bg-muted animate-pulse rounded" />
                                <div className="h-4 bg-muted animate-pulse rounded" />
                            </div>
                            <div className="flex items-center gap-x-1">
                                <div className="w-7 h-7 bg-muted animate-pulse rounded" />
                                <div className="w-7 h-7 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    ))
                ) : workOrders.length > 0 ? (
                    workOrders.map((item, index) => (
                        <div
                            key={index}
                            className="border p-4 space-y-4 rounded"
                        >
                            <div className="flex justify-between gap-1 items-start">
                                <div className="space-y-1 min-w-0">
                                    <h3 className="text-muted-foreground text-xs">Homeowner Name</h3>
                                    <Link
                                        href={item.home?.homeowner?.id
                                            ? buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(item.home.homeowner.id))
                                            : WORK_ORDERS_ROUTES.details(item.id)}
                                        className="cursor-pointer hover:underline block truncate"
                                    >
                                        {session?.role === UserRole.Supporter
                                            ? maskHomeownerLastName(item.home?.homeowner?.user?.fullname || "No homeowner")
                                            : (item.home?.homeowner?.user?.fullname || "No homeowner")}
                                    </Link>
                                </div>
                                <div className="space-y-1 shrink-0">
                                    {item.status === "Active" ? (
                                        <Badge
                                            variant="default"
                                            className="bg-primary/15 text-foreground border-0 h-[22px] rounded-full flex items-center gap-2 justify-center w-fit font-medium"
                                        >
                                            <span className="w-1.5 h-1.5 aspect-square rounded-full bg-green-600"></span>
                                            Open
                                        </Badge>
                                    ) : (
                                        <Badge
                                            variant="outline"
                                            className="border-foreground rounded-full flex items-center h-[22px] gap-2 justify-center w-fit font-medium"
                                        >
                                            <span className="w-1.5 h-1.5 aspect-square rounded-full bg-foreground"></span>
                                            Closed
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4">
                                <OwnerInfoMobile
                                    infoTitle="Homeowner Address"
                                    infoValue={
                                        session?.role === UserRole.Supporter
                                            ? maskHomeownerAddress(item.home?.address)
                                            : (item.home?.address || "No address")
                                    }
                                    href={(() => {
                                        if (!item.home?.id) return undefined
                                        if (session?.role === UserRole.Supporter) return undefined
                                        const googleUrl = buildGoogleMapsPlaceUrl(
                                            item.home.address,
                                            item.home.latitude ?? null,
                                            item.home.longitude ?? null,
                                        )
                                        if (googleUrl !== "#") return googleUrl
                                        return buildRegionAwareHref(
                                            regionSlug,
                                            `${HOMEOWNERS_ROUTES.map}&homeId=${item.home.id}`,
                                        )
                                    })()}
                                />
                                <OwnerInfoMobile
                                    infoTitle="Phone Number"
                                    infoValue={
                                        session?.role === UserRole.Supporter
                                            ? maskHomeownerPhone(item.home?.homeowner?.user?.phone)
                                            : (item.home?.homeowner?.user?.phone ?? "N/A")
                                    }
                                    href={session?.role === UserRole.Supporter ? undefined : (getPhoneTelLink(item.home?.homeowner?.user?.phone) ?? undefined)}
                                />
                            </div>
                            <div className="grid grid-cols-2">
                                <Link href={WORK_ORDERS_ROUTES.details(item.id)} className="cursor-pointer hover:underline">
                                    <OwnerInfoMobile
                                        infoTitle="Work Order"
                                        infoValue={item.workScope?.workScopeDisplay || formatWorkScope(item.workOrderType)}
                                    />
                                </Link>
                                <OwnerInfoMobile
                                    infoTitle="Created Date"
                                    infoValue={
                                        item.createdAt
                                            ? new Date(
                                                  item.createdAt
                                              ).toLocaleDateString()
                                            : "No date"
                                    }
                                />
                            </div>
                            <div className="grid grid-cols-2">
                                <div className="space-y-1">
                                    <h3 className="text-muted-foreground text-xs">
                                        Owner
                                    </h3>
                                    <div className="flex gap-x-1">
                                        <Avatar className="flex items-center justify-center bg-primary text-white w-8 h-8">
                                            <AvatarImage src={item.owner?.profilePicture ?? undefined} alt="" className="object-cover object-top" />
                                            <AvatarFallback className="">
                                                {item.owner
                                                    ? item.owner.fullname
                                                          .split(" ")
                                                          .map(
                                                              (n: string) =>
                                                                  n[0]
                                                          )
                                                          .join("")
                                                    : "NA"}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex flex-col justify-center font-medium">
                                            <p className="text-foreground text-xs">
                                                {item.owner
                                                    ? item.owner.fullname
                                                    : "Not assigned"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-muted-foreground text-xs">
                                        Team Lead
                                    </h3>
                                    <div className="flex gap-x-1">
                                        <Avatar className="flex items-center justify-center bg-primary text-white w-8 h-8">
                                            <AvatarImage src={item.teamLead?.profilePicture ?? undefined} alt="" className="object-cover object-top" />
                                            <AvatarFallback className="">
                                                {item.teamLead
                                                    ? item.teamLead.fullname
                                                          .split(" ")
                                                          .map(
                                                              (n: string) =>
                                                                  n[0]
                                                          )
                                                          .join("")
                                                    : "NA"}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex flex-col justify-center font-medium">
                                            <p className="text-foreground text-xs">
                                                {item.teamLead
                                                    ? item.teamLead.fullname
                                                    : "Not assigned"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Matches Homeowners etc: primary buttons left, toggle + edit + map + delete right-justified. -ml-2 minimizes left whitespace before View on mobile. */}
                            <div className="flex items-center justify-between -ml-2">
                                <div className="flex items-center gap-2">
                                    <Link href={WORK_ORDERS_ROUTES.details(item.id)}>
                                        <Button
                                            variant="default"
                                            size="sm"
                                            className="bg-primary hover:bg-primary/90 text-white font-bold h-6 px-2 text-xs"
                                        >
                                            View
                                        </Button>
                                    </Link>
                                </div>
                                <div className="flex items-center gap-x-1">
                                    {canManageWorkOrders && (
                                        <StatusToggleSwitch
                                            workOrderId={item.id}
                                            currentStatus={item.status}
                                            onStatusChange={handleStatusChange}
                                        />
                                    )}
                                    {canManageWorkOrders && (
                                        <Button
                                            variant="link"
                                            size="icon"
                                            className="w-7 h-7 min-w-7 min-h-7 flex-shrink-0 bg-foreground rounded-md hover:bg-foreground/90"
                                            asChild
                                        >
                                            <Link href={WORK_ORDERS_ROUTES.edit(item.id)}>
                                                <Pencil className="w-4 text-white" />
                                            </Link>
                                        </Button>
                                    )}
                                    {item.home?.id && (
                                        <Button
                                            variant="link"
                                            size="icon"
                                            className="w-7 h-7 min-w-7 min-h-7 flex-shrink-0 bg-foreground rounded-md hover:bg-foreground/90"
                                            asChild
                                        >
                                            <Link href={buildRegionAwareHref(regionSlug, `${HOMEOWNERS_ROUTES.map}&homeId=${item.home.id}`)}>
                                                <MapPin className="w-4 text-white" />
                                            </Link>
                                        </Button>
                                    )}
                                    {canManageWorkOrders && (
                                        <WorkOrderDeleteDialogWrapper
                                            workOrderId={item.id}
                                            orderNumber={item.orderNumber}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-8 text-muted-foreground">
                        {emptyStateMessage}
                    </div>
                )}
            </div>
        </div>
    )
}

export default WorkOrdersCardsMobile
